import { useMemo, useState, useEffect } from 'react'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { Box } from '@mui/system'
import ProtectedStatusRoute from 'app/ProtectedStatusRoute'
import ProtectedUserRoute from 'app/ProtectedUserRoute'
import ErrorDisplay from 'components/ErrorDisplay'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import NoProcedure from 'components/NoProcedure'
import ProcedureStatusBar from 'components/ProcedureStatusBar'
import AddRepModal from 'components/AddRepModal'
import ErrorFallback from 'views/ErrorFallback/ErrorFallback'
import {
  useBatchAddAssetsToSurgery,
  useGetSurgeryQuery,
} from 'lib/apollo/hooks'
import { deriveSurgeryStatus } from 'lib/utils/data'
import { AssetFormDataProvider } from 'lib/context/AssetFormData'
import AddAsset from 'views/assets/AddAsset'
import AssetList from 'views/AssetList/AssetList'
import DeleteAssets from 'views/DeleteAssets/DeleteAssets'
import styles from './procedure.module.css'
import { Surgery } from 'common/types'
import PermissionDenied from 'components/PermissionDenied'
import { InstrumentTrays } from 'views/InstrumentTrays/InstrumentTrays'
import { SingleScanProvider } from 'components/SingleScanProvider'
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  CircularProgress,
} from '@mui/material'
import MainIconLanding from 'views/MainIconLanding/MainIconLanding'
import TrayIconLanding from 'views/IconLandingPages/TrayIconLanding'
import { House, List, LowPriority } from '@mui/icons-material'
import { ImplantSitesProvider } from 'lib/context/ImplantSitesContext'
import { useUndocumentedAssetsContext } from 'lib/context/UndocumentedAssetsContext'
import { BatchScanning } from 'views/BatchScan/BatchScanning'
import CaptureCamera from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/CaptureCamera'
import AssignedDigitalTrays from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays'
import TrayVisualization from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/TrayVisualization'
import _ from 'lodash'
import InventoryProvider from 'lib/apollo/inventory-config'
import { CaptureTrayContextProvider } from 'views/DigitalTrayMapping/CaptureTray/CaptureTray.context'
import { EditDocumentedListProvider } from 'lib/context/EditDocumentedListContext/EditDocumentedListContext'
import PreferenceCard from 'views/PreferenceCard/PreferenceCard'
import { RfSpongeCountProvider } from 'lib/context/RfSpongeCountContext/RfSpongeCountContext'
import { useUser } from 'app/User'
import { getGroupedAssetData } from 'lib/utils/grouped-asset-data'
import { useAssetType } from 'lib/context/AssetTypeContext/AssetTypeContext'
import { closeSnackbar } from 'notistack'
import { SPDCortexScannerProvider } from 'views/SPDLayout/SPD/SPDCortexScanner/SPDCortexScanner.context'
import TrayProductsPassthrough from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayProducts/TrayProductsPassthrough'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import { useUpdateGuestRepInfo } from 'lib/services/api/getAccessToken'
import LoadingState from 'views/GuestAccess/LoadingState'
import ProductRequestForm from 'views/assets/ManualAssetEntry/ProductRequestForm/ProductRequestForm'
import { useSurgeryActions } from 'store/notifications'
import AssetMissMatchModal from 'views/assets/AssetMissMatchModal/AssetMissMatchModal'
import { useEnterpriseStore } from 'store'

function Procedure() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as {
    canAddAsset: boolean
    companyId: string
    isGuest: boolean
  }

  const { setSurgeryStatus } = useSurgeryActions()

  const { surgeryId } = useParams()
  const {
    isBatchMode,
    isSalesRepQrOpen,
    mainIconFlowState,
    onMainIconLanding,
    onTrayIconLanding,
    setAssetType,
    setIsBatchMode,
    setIsSalesRepQrOpen,
    setMainIconFlowState,
    setOnMainIconLanding,
    setOnTrayIconLanding,
  } = useAssetType()

  const [userIsPermitted, setUserIsPermitted] = useState<boolean>(true)

  const { undocumentedScanCount, isLoadingUndocumentedList } =
    useUndocumentedAssetsContext()

  // Add Sales Rep QR
  const handleCloseQrModal = () => {
    setIsSalesRepQrOpen(false)
  }

  const resetToProcedureLanding = () => {
    setOnMainIconLanding(true)
    setOnTrayIconLanding(false)
    setIsBatchMode(false)
    setMainIconFlowState({
      isBatchModeEnabled: true,
      isMultipackHardware: false,
      multipackHardwareSelectedResult: undefined,
    })
    navigate('')
    setAssetType(null)
  }

  const { isRep, isGuest, user } = useUser()

  useEffect(() => {
    if (!user?.id) {
      window.location.reload()
    }
  }, [user])

  const repCompanyIds = useMemo(() => {
    const companyIds = isRep
      ? [
          ...(user?.bidCompanyId ? [user.bidCompanyId] : []),
          ...(user?.parentCompanyId ? [user.parentCompanyId] : []),
          ...(user?.siblingCompaniesIds ?? []),
          ...(user?.subsidiariesCompaniesIds ?? []),
          ...(user?.subsidiariesIds ?? []),
        ]
      : []
    return companyIds
  }, [
    isRep,
    user?.bidCompanyId,
    user?.parentCompanyId,
    user?.siblingCompaniesIds,
    user?.subsidiariesCompaniesIds,
    user?.subsidiariesIds,
  ])

  const surgeryQuery = useGetSurgeryQuery(
    surgeryId,
    {
      disablePolling: false,
      fetchPolicy: 'no-cache',
    },
    repCompanyIds
  )

  const { selectedDepartment } = useEnterpriseStore()

  const [batchAddAssetsToSurgery, batchAddAssetsToSurgeryMutation] =
    useBatchAddAssetsToSurgery(surgeryId ?? '', selectedDepartment?.id)

  const [isLoadingDocumentedList, setIsLoadingDocumentedList] = useState(false)

  // monitor when batchAddAssetsToSurgeryMutation is loading to render spinner in bottom nav
  useEffect(() => {
    if (batchAddAssetsToSurgeryMutation.loading) {
      setIsLoadingDocumentedList(true)
    }
  }, [batchAddAssetsToSurgeryMutation.loading])

  useEffect(() => {
    if (isLoadingDocumentedList && batchAddAssetsToSurgeryMutation.data) {
      setIsLoadingDocumentedList(false)
    }
  }, [isLoadingDocumentedList, batchAddAssetsToSurgeryMutation.data])

  // @ts-ignore
  const scanMode = location.state?.mode
  const navigationValue = location.pathname.replace(
    `/surgery/${surgeryId}/`,
    ''
  )

  // clean up url pathname on each page load / refresh
  useEffect(() => {
    navigate(`/surgeries/${surgeryId}`, {
      replace: true,
      state: {
        canAddAsset: state?.canAddAsset,
        companyId: state?.companyId,
        isGuest: state?.isGuest,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (scanMode) {
      setIsBatchMode(scanMode === 'batch')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode, isBatchMode])

  const surgery = surgeryQuery.data?.getSurgery as Surgery

  const groupedAssetData = getGroupedAssetData(surgery?.assetGroups ?? [])

  const totalDocumentedAssets =
    (!isRep ? groupedAssetData.biologic.nurseScans.length : 0) +
    (!isRep ? groupedAssetData.biologic.repScans.length : 0) +
    groupedAssetData.consumable.repScans.length +
    groupedAssetData.consumable.nurseScans.length +
    groupedAssetData.explanted.length +
    groupedAssetData.hardware.approved.length +
    groupedAssetData.hardware.nurseScans.length +
    groupedAssetData.hardware.repScans.length +
    groupedAssetData.other.nurseScans.length +
    groupedAssetData.other.repScans.length

  // TODO: uniq is short term fix for duplicate asset groups
  const derivedSurgeryStatus = useMemo(
    () =>
      deriveSurgeryStatus({
        statusName: surgery?.status?.name,
        assetGroups: _.uniq(surgery?.assetGroups),
      }),
    [surgery]
  )

  const isProcedureSubmitted = derivedSurgeryStatus.name === 'SUBMITTED'
  const assetsCompanies = useMemo(
    () =>
      Array.from(
        new Set(
          surgery?.assetGroups?.map((asset) => asset.bidCompanyId as number)
        )
      ),
    [surgery]
  )

  useEffect(() => {
    if (
      scanMode &&
      !isProcedureSubmitted &&
      mainIconFlowState.isBatchModeEnabled
    ) {
      setIsBatchMode(scanMode === 'batch')
    }
    if (isProcedureSubmitted) {
      setOnMainIconLanding(false)
      navigate(`/surgeries/${surgeryId}/list`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scanMode,
    isBatchMode,
    isProcedureSubmitted,
    navigate,
    surgeryId,
    mainIconFlowState.isBatchModeEnabled,
  ])

  const [userCanScan, setUserCanScan] = useState<boolean>(true)

  useEffect(() => {
    if (location.pathname.includes('result')) {
      setUserCanScan(false)
    } else {
      setUserCanScan(true)
    }
  }, [location.pathname])

  const { mutateAsync: updateGuestRepInfo, isLoading: isUpdatingGuestRepInfo } =
    useUpdateGuestRepInfo()

  const userCanAccess = true

  const [alertMessage, setAlertMessage] = useState<{
    show: boolean
    trigger: string | null
  }>({
    show: false,
    trigger: null,
  })

  useEffect(() => {
    setSurgeryStatus(surgery?.status?.name)
    if (isRep && surgery?.authorizedReps) {
      const accessToken = JSON.parse(
        localStorage.getItem('auth_response') || '{}'
      ).access_token
      const userInfo = JSON.parse(localStorage.getItem('guest_user') || '{}')
      const verificationToken = localStorage.getItem('verification_token') || ''
      const caseId = surgeryId

      const authorizedRep = surgery.authorizedReps.find(
        (rep: { id: string }) => rep.id === user?.id
      )
      if (authorizedRep) {
        const repAccessLevel = authorizedRep?.accessLevel
        setUserIsPermitted(repAccessLevel === 1)
        closeSnackbar()
        setOnMainIconLanding(false)
        setOnTrayIconLanding(false)
        if (repAccessLevel === 1) {
          if (derivedSurgeryStatus.name === 'PRE_APPROVAL') {
            navigate(`/surgeries/${surgeryId}/list`)
          }
        } else {
          navigate(`/surgeries/${surgeryId}/list`)
        }
      } else {
        // This flow is for guest reps.
        // This logic will get the updated rep info after migrating them to auth0
        if (
          isGuest &&
          derivedSurgeryStatus.name === 'SUBMITTED' &&
          !userInfo?.isMigrated
        ) {
          updateGuestRepInfo(
            {
              userId: userInfo.id,
              verificationToken,
              accessToken,
            },
            {
              onSuccess: (response) => {
                localStorage.setItem('auth_response', JSON.stringify(response))
                localStorage.setItem(
                  'guest_user',
                  JSON.stringify(response.payload)
                )
                localStorage.setItem('verification_token', verificationToken)
                navigate(`/surgeries/${caseId}`, {
                  state: {
                    canAddAsset: userInfo?.canAddAsset,
                    companyId: userInfo?.companyId,
                    isGuest: userInfo?.isGuestRep,
                  },
                })
              },
              onError: (error) => {
                console.error('Error updating guest rep info:', error)
                navigate('/home', { replace: true })
              },
            }
          )
        } else {
          navigate(`/surgeries`)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedSurgeryStatus, surgery, navigate, surgeryId, user, isGuest])

  if (isUpdatingGuestRepInfo) {
    return (
      <LoadingState
        title="Getting Updated Surgery Info"
        message="Please wait while we update your information..."
      />
    )
  }

  const navigateToUndocumentedList = () => {
    closeSnackbar()
    setOnMainIconLanding(false)
    setOnTrayIconLanding(false)
    navigate('asset/batch/undocumented')
  }

  const navigateToDocumentedList = () => {
    closeSnackbar()
    setOnMainIconLanding(false)
    setOnTrayIconLanding(false)
    navigate(`/surgeries/${surgeryId}/list`)
  }

  if (surgeryQuery.loading) {
    return <LoadingIndicator text="Loading surgery details" />
  }

  if (surgeryQuery.error) {
    if (
      surgeryQuery.error.message.includes('Invalid surgeryId') ||
      surgeryQuery.error.message.includes('No Surgery found with id')
    ) {
      return <NoProcedure />
    }
  }

  if (!userCanAccess) {
    return <PermissionDenied />
  }

  return (
    <ImplantSitesProvider>
      <AssetFormDataProvider>
        {!surgeryQuery.loading && <ProcedureStatusBar surgery={surgery} />}
        <RfSpongeCountProvider surgery={surgery}>
          <CaptureTrayContextProvider>
            <AlertDialog
              isOpen={alertMessage.show}
              mode="warning"
              title="Discard Documentation?"
              description="Scroll down to add this product to documented list. Exiting will discard all your current progress. Are you sure you want to exit?"
              secondaryButtonText="Cancel"
              secondaryButtonAction={() => {
                setAlertMessage({ show: false, trigger: null })
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: 'smooth',
                })
              }}
              primaryButtonText="Exit"
              primaryButtonAction={() => {
                setAlertMessage((prev) => {
                  if (prev.trigger === 'resetToProcedureLanding')
                    resetToProcedureLanding()
                  if (prev.trigger === 'navigateToUndocumentedList')
                    navigateToUndocumentedList()
                  if (prev.trigger === 'navigateToDocumentedList')
                    navigateToDocumentedList()
                  return { show: false, trigger: null }
                })
              }}
              zIndex={999}
            />
            {onMainIconLanding && !isProcedureSubmitted && (
              <MainIconLanding
                setOnMainIconLanding={setOnMainIconLanding}
                surgery={surgery}
                derivedSurgeryStatus={derivedSurgeryStatus.name}
              />
            )}

            {onTrayIconLanding && (
              <TrayIconLanding
                mainIconFlowState={mainIconFlowState}
                setMainIconFlowState={setMainIconFlowState}
                setOnTrayIconLanding={setOnTrayIconLanding}
              />
            )}

            {/* REP QR INVITE MODAL */}
            <AddRepModal
              isOpen={isSalesRepQrOpen}
              onClose={handleCloseQrModal}
              surgeryId={surgery?._id}
              assetsCompanies={assetsCompanies}
            />

            <AssetMissMatchModal />

            {!onMainIconLanding && !onTrayIconLanding && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="center"
                flex={1}
              >
                <Box
                  maxWidth="768px"
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  flex={1}
                  position="relative"
                >
                  <Routes>
                    <Route
                      path="preference-card/*"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={[
                            'SCANNING',
                            'APPROVED',
                            'PRE_APPROVAL',
                          ]}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute
                            roles={['MGAT_NURSEADMIN', 'MGAT_NURSE']}
                          >
                            <PreferenceCard />
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="asset/batch/*"
                      element={
                        <ProtectedUserRoute isPermitted={userIsPermitted}>
                          <BatchScanning
                            setIsBatchMode={setIsBatchMode}
                            surgery={surgery}
                            batchAddAssetsToSurgery={batchAddAssetsToSurgery}
                            batchAddAssetsToSurgeryMutation={
                              batchAddAssetsToSurgeryMutation
                            }
                            setMainIconFlowState={setMainIconFlowState}
                          />
                        </ProtectedUserRoute>
                      }
                    />
                    <Route
                      path="asset/*"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={[
                            'SCANNING',
                            'APPROVED',
                            'PRE_APPROVAL',
                          ]}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute isPermitted={true}>
                            <SingleScanProvider
                              canScan={userCanScan}
                              isTrayScan={location.pathname.includes('manual')}
                            >
                              <AddAsset
                                assetGroups={surgery.assetGroups}
                                userCanScan={userCanScan}
                                setUserCanScan={setUserCanScan}
                                surgery={surgery}
                                mainIconFlowState={mainIconFlowState}
                                isBatchMode={isBatchMode}
                              />
                            </SingleScanProvider>
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="list"
                      element={
                        surgeryQuery.error ? (
                          <ErrorDisplay pathForward={surgeryQuery.refetch} />
                        ) : (
                          <EditDocumentedListProvider>
                            <AssetList
                              surgery={surgery}
                              derivedSurgeryStatus={derivedSurgeryStatus}
                            />
                          </EditDocumentedListProvider>
                        )
                      }
                    />
                    <Route
                      path="asset/batch/create"
                      element={
                        <ProductRequestForm surgeryId={surgeryId as string} />
                      }
                    />
                    <Route
                      path="instrument-trays"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <SingleScanProvider
                              canScan={userCanScan}
                              isTrayScan={true}
                            >
                              <InstrumentTrays surgery={surgery} />
                            </SingleScanProvider>
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="assigned-digital-trays/*"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <SPDCortexScannerProvider>
                              <InventoryProvider>
                                <AssignedDigitalTrays surgery={surgery} />
                              </InventoryProvider>
                            </SPDCortexScannerProvider>
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="capture-tray/:trayID"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <CaptureCamera />
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="tray-products"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <TrayProductsPassthrough />
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="tray-visualization"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <InventoryProvider>
                              <TrayVisualization />
                            </InventoryProvider>
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="delete"
                      element={
                        <ProtectedStatusRoute
                          permittedStatus={['SCANNING', 'APPROVED']}
                          status={derivedSurgeryStatus.name}
                        >
                          <ProtectedUserRoute roles={['MGAT_NURSE']}>
                            <DeleteAssets surgery={surgery} />
                          </ProtectedUserRoute>
                        </ProtectedStatusRoute>
                      }
                    />
                    <Route
                      path="/"
                      element={<MainIconLanding surgery={surgery} />}
                    />
                    <Route path="*" element={<ErrorFallback />} />
                  </Routes>
                </Box>
              </Box>
            )}
            <BottomNavigation
              id="asset-list-footer"
              className={styles.bottomNavigation}
              showLabels
              value={navigationValue}
              sx={{
                height: '70px',
              }}
            >
              <BottomNavigationAction
                data-testid="home-button"
                onClick={() => {
                  const url = window.location.pathname
                  const objectIdPattern = /^[a-f\d]{24}$/i
                  const parts = url.split('/')
                  const lastSegment = parts[parts.length - 1]
                  const secondLastSegment = parts[parts.length - 2]

                  if (
                    !(
                      secondLastSegment === 'surgeries' &&
                      objectIdPattern.test(lastSegment)
                    ) &&
                    !window.location.pathname.endsWith('/list') &&
                    !window.location.pathname.endsWith('/undocumented') &&
                    !window.location.pathname.endsWith('/scan') &&
                    !window.location.pathname.endsWith('/batch') &&
                    !window.location.pathname.endsWith('/instrument-trays') &&
                    !window.location.pathname.endsWith('/preference-card') &&
                    !window.location.pathname.endsWith(
                      '/assigned-digital-trays'
                    )
                  ) {
                    setAlertMessage({
                      show: true,
                      trigger: 'resetToProcedureLanding',
                    })
                  } else {
                    resetToProcedureLanding()
                  }
                }}
                label="Home"
                icon={
                  <House
                    sx={{
                      fontSize: '1.8rem',
                      color:
                        !navigationValue.includes('list') &&
                        !navigationValue.includes('undocumented')
                          ? 'var(--primary)'
                          : 'inherit',
                    }}
                  />
                }
                sx={{
                  '.MuiBottomNavigationAction-label': {
                    fontSize: '13px !important',
                    fontWeight: '500',
                    color:
                      !navigationValue.includes('list') &&
                      !navigationValue.includes('undocumented')
                        ? 'var(--primary)'
                        : 'inherit',
                  },
                  maxWidth: `${450 / 2}px`,
                  opacity:
                    !userIsPermitted ||
                    isProcedureSubmitted ||
                    (isRep && derivedSurgeryStatus.name === 'PRE_APPROVAL')
                      ? 0.5
                      : 1,
                  '& .MuiBottomNavigationAction-root': {
                    color: 'gray !important',
                  },
                  ...(!navigationValue.includes('list') &&
                    !navigationValue.includes('undocumented') && {
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        backgroundColor: 'blue',
                      },
                    }),
                }}
                disabled={
                  !userIsPermitted ||
                  isProcedureSubmitted ||
                  (isRep && derivedSurgeryStatus.name === 'PRE_APPROVAL')
                }
              />

              <BottomNavigationAction
                onClick={() => {
                  const url = window.location.pathname
                  const objectIdPattern = /^[a-f\d]{24}$/i
                  const parts = url.split('/')
                  const lastSegment = parts[parts.length - 1]
                  const secondLastSegment = parts[parts.length - 2]

                  if (
                    !(
                      secondLastSegment === 'surgeries' &&
                      objectIdPattern.test(lastSegment)
                    ) &&
                    !window.location.pathname.endsWith('/list') &&
                    !window.location.pathname.endsWith('/undocumented') &&
                    !window.location.pathname.endsWith('/scan') &&
                    !window.location.pathname.endsWith('/batch') &&
                    !window.location.pathname.endsWith('/instrument-trays') &&
                    !window.location.pathname.endsWith('/preference-card') &&
                    !window.location.pathname.endsWith(
                      '/assigned-digital-trays'
                    )
                  ) {
                    setAlertMessage({
                      show: true,
                      trigger: 'navigateToUndocumentedList',
                    })
                  } else {
                    navigateToUndocumentedList()
                  }
                }}
                disabled={
                  !userIsPermitted ||
                  isProcedureSubmitted ||
                  (isRep && derivedSurgeryStatus.name === 'PRE_APPROVAL')
                }
                data-testid="undocumented-button"
                value="asset/batch/undocumented"
                label="Undocumented"
                icon={
                  isLoadingUndocumentedList ? (
                    <CircularProgress />
                  ) : (
                    <Badge
                      // badgeContent={undocumentedScanCount}
                      badgeContent={undocumentedScanCount}
                      color={undocumentedScanCount > 0 ? 'error' : 'default'}
                      sx={{
                        '& .MuiBadge-badge': {
                          transform: 'translate(13px, -5px)',
                          color: 'white',
                        },
                      }}
                    >
                      <LowPriority
                        sx={{
                          fontSize: '1.8rem',
                          color: navigationValue.includes('undocumented')
                            ? 'var(--primary)'
                            : 'inherit',
                        }}
                      />
                    </Badge>
                  )
                }
                sx={{
                  '.MuiBottomNavigationAction-label': {
                    fontSize: '13px !important',
                    fontWeight: '500',
                    color: navigationValue.includes('undocumented')
                      ? 'var(--primary)'
                      : 'inherit',
                  },
                  maxWidth: `${450 / 2}px`,
                  '> span': {
                    fontSize: '11px !important',
                  },
                  opacity:
                    !userIsPermitted ||
                    isProcedureSubmitted ||
                    (isRep && derivedSurgeryStatus.name === 'PRE_APPROVAL')
                      ? 0.5
                      : 1,
                  ...(navigationValue.includes('undocumented') && {
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'blue',
                    },
                  }),
                }}
              />
              <BottomNavigationAction
                data-testid="documented-button"
                onClick={() => {
                  const url = window.location.pathname
                  const objectIdPattern = /^[a-f\d]{24}$/i
                  const parts = url.split('/')
                  const lastSegment = parts[parts.length - 1]
                  const secondLastSegment = parts[parts.length - 2]

                  if (
                    !(
                      secondLastSegment === 'surgeries' &&
                      objectIdPattern.test(lastSegment)
                    ) &&
                    !window.location.pathname.endsWith('/list') &&
                    !window.location.pathname.endsWith('/undocumented') &&
                    !window.location.pathname.endsWith('/scan') &&
                    !window.location.pathname.endsWith('/batch') &&
                    !window.location.pathname.endsWith('/instrument-trays') &&
                    !window.location.pathname.endsWith('/preference-card') &&
                    !window.location.pathname.endsWith(
                      '/assigned-digital-trays'
                    )
                  ) {
                    setAlertMessage({
                      show: true,
                      trigger: 'navigateToDocumentedList',
                    })
                  } else {
                    navigateToDocumentedList()
                  }
                }}
                label="Documented"
                icon={
                  isLoadingDocumentedList ? (
                    <CircularProgress />
                  ) : (
                    <Badge
                      badgeContent={totalDocumentedAssets}
                      color={totalDocumentedAssets > 0 ? 'primary' : 'default'}
                      sx={{
                        '& .MuiBadge-badge': {
                          transform: 'translate(10px, -5px)',
                          color: 'white',
                        },
                      }}
                    >
                      <List
                        sx={{
                          fontSize: '1.8rem',
                          color: navigationValue.includes('list')
                            ? 'var(--primary)'
                            : 'inherit',
                        }}
                      />
                    </Badge>
                  )
                }
                sx={{
                  maxWidth: `${450 / 2}px`,
                  '.MuiBottomNavigationAction-label': {
                    color: navigationValue.includes('list')
                      ? 'var(--primary)'
                      : 'inherit',
                    fontSize: '13px !important',
                    fontWeight: '500',
                  },
                  ...(navigationValue.includes('list') && {
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'blue',
                    },
                  }),
                }}
              />
            </BottomNavigation>
          </CaptureTrayContextProvider>
        </RfSpongeCountProvider>
      </AssetFormDataProvider>
    </ImplantSitesProvider>
  )
}
export default Procedure
